import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AUTH_ENTRY_URL } from '@core/auth/auth-entry.constants';
import { isPublicAuthHttpRequest } from '@core/auth/auth-http-error.util';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { AuthTokenRefreshService } from '@core/auth/auth-token-refresh.service';
import { PUBLIC_CATALOG_REQUEST } from '@core/http/public-catalog-http.context';
import {
  companyRequiredRedirectPath,
  isCompanyRequiredHttpError,
  resolveCompanyRequiredRedirect,
} from '@core/http/company-required-redirect.util';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { LocationContextService } from '@core/tenant/location-context.service';

function clearSessionLocally(
  authSession: AuthSessionService,
  tenantContext: TenantContextService,
  locationContext: LocationContextService
): void {
  authSession.clear();
  tenantContext.clear();
  locationContext.clear();
}

export const apiErrorInterceptor: HttpInterceptorFn = (req, next: HttpHandlerFn) => {
  const router = inject(Router);
  const authSession = inject(AuthSessionService);
  const tenantContext = inject(TenantContextService);
  const locationContext = inject(LocationContextService);
  const tokenRefresh = inject(AuthTokenRefreshService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Storefront público: un 401 aca (ej. token viejo de otra sesión en localStorage)
      // nunca debe limpiar la sesión ni mandar al visitante anónimo a "sesión expirada".
      if (error.status === 401 && req.context.get(PUBLIC_CATALOG_REQUEST)) {
        return throwError(() => error);
      }

      if (error.status === 401 && !isPublicAuthHttpRequest(req) && !req.url.includes('/api/v1/auth/refresh')) {
        return tokenRefresh.tryRefresh().pipe(
          switchMap((ok) => {
            if (!ok) {
              clearSessionLocally(authSession, tenantContext, locationContext);
              void router.navigate(AUTH_ENTRY_URL, {
                replaceUrl: true,
                queryParams: { reason: 'session-expired' },
              });
              return throwError(() => error);
            }
            const token = authSession.token();
            const retry = token
              ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
              : req;
            return next(retry);
          })
        );
      }

      if (error.status === 401) {
        if (isPublicAuthHttpRequest(req)) {
          if (authSession.isAuthenticated()) {
            clearSessionLocally(authSession, tenantContext, locationContext);
          }
        } else {
          clearSessionLocally(authSession, tenantContext, locationContext);
          void router.navigate(AUTH_ENTRY_URL, {
            replaceUrl: true,
            queryParams: { reason: 'session-expired' },
          });
        }
      }
      if (isCompanyRequiredHttpError(error)) {
        const redirect = resolveCompanyRequiredRedirect(
          authSession.isAuthenticated(),
          authSession.user(),
          tenantContext
        );
        const destination = companyRequiredRedirectPath(redirect);
        if (destination) {
          void router.navigate(destination, { replaceUrl: true });
        }
      }
      return throwError(() => error);
    })
  );
};
